import joi from 'joi';
import { EventName, EventSource } from '../../api-client';
import { MappedEarnEvent } from '../../ingestor-types';

export enum IncomingWebhookType {
  MAPPED_EVENT = 'MAPPED_EVENT',
}

export enum IncomingWebhookAuthenticationType {
  HEADER = 'HEADER',
  QUERY_PARAM = 'QUERY_PARAM',
  HUB_SIGNATURE = 'HUB_SIGNATURE',
}

export type IncomingWebhookAuthenticationConfig =
  | IncomingWebhookHubSignatureAuthenticationConfig
  | IncomingWebhookHeaderAuthenticationConfig
  | IncomingWebhookQueryParamAuthenticationConfig
  | MetaAuthenticationConfig;

export interface IncomingWebhookHubSignatureAuthenticationConfig {
  type: IncomingWebhookAuthenticationType.HUB_SIGNATURE;
  secret: string;
}

export const $incomingWebhookHubSignatureAuthenticationConfig =
  joi.object<IncomingWebhookHubSignatureAuthenticationConfig>({
    type: joi.string().valid(IncomingWebhookAuthenticationType.HUB_SIGNATURE),
    secret: joi.string(),
  });

export interface MetaAuthenticationConfig extends IncomingWebhookHubSignatureAuthenticationConfig {
  verificationSecret: string;
}

export const $metaAuthenticationConfig = $incomingWebhookHubSignatureAuthenticationConfig.concat(
  joi.object<MetaAuthenticationConfig>({
    verificationSecret: joi.string(),
  }),
);

export interface IncomingWebhookHeaderAuthenticationConfig {
  type: IncomingWebhookAuthenticationType.HEADER;
  header: string;
  token: string;
}

export const $incomingWebhookHeaderAuthenticationConfig =
  joi.object<IncomingWebhookHeaderAuthenticationConfig>({
    type: joi.string().valid(IncomingWebhookAuthenticationType.HEADER),
    header: joi.string(),
    token: joi.string(),
  });

export interface IncomingWebhookQueryParamAuthenticationConfig {
  type: IncomingWebhookAuthenticationType.QUERY_PARAM;
  key: string;
  token: string;
}

export const $incomingWebhookQueryParamAuthenticationConfig =
  joi.object<IncomingWebhookQueryParamAuthenticationConfig>({
    type: joi.string().valid(IncomingWebhookAuthenticationType.QUERY_PARAM),
    key: joi.string(),
    token: joi.string(),
  });

export interface IncomingIngestorWebhookAttributes {
  id: string;
  accountId: string;
  programId: string;
  type: IncomingWebhookType;
  name: string;
  authenticationConfig: IncomingWebhookAuthenticationConfig | null;
  integrationId?: string | null;
  config?: IncomingWebhookConfig | null;
  url: string;
  createdAt: string;
  updatedAt: string;
  // Used for backoffice frontend
  isNew?: boolean;
}

export interface AddIngestorIncomingWebhookBody {
  id: string;
  type: IncomingWebhookType;
  name: string;
  authenticationConfig?: IncomingWebhookAuthenticationConfig | null;
  integrationId?: string | null;
  accountId: string;
  isNew: true;
}

const $authenticationConfig = joi
  .when('type', {
    is: IncomingWebhookAuthenticationType.HUB_SIGNATURE,
    then: $incomingWebhookHubSignatureAuthenticationConfig,
  })
  .when('type', {
    is: IncomingWebhookAuthenticationType.HEADER,
    then: $incomingWebhookHeaderAuthenticationConfig,
  })
  .when('type', {
    is: IncomingWebhookAuthenticationType.QUERY_PARAM,
    then: $incomingWebhookQueryParamAuthenticationConfig,
  });

const $integrationId = joi.string().allow(null, '').optional();

export const $authenticationConfigUnlessIntegration = (integrationIdPath: string) =>
  joi.when(integrationIdPath, {
    is: joi.string().min(1),
    then: $authenticationConfig.allow(null).optional(),
    otherwise: $authenticationConfig,
  });

export const $addIncomingWebhookBody = joi.object<AddIngestorIncomingWebhookBody>({
  type: joi.string().valid(...Object.values(IncomingWebhookType)),
  name: joi.string(),
  authenticationConfig: $authenticationConfigUnlessIntegration('integrationId'),
  integrationId: $integrationId,
  accountId: joi.string(),
  isNew: joi.boolean().default(true),
});

export interface UpdateIngestorIncomingWebhookBody {
  id: string;
  name: string;
  authenticationConfig?: IncomingWebhookAuthenticationConfig | null;
  integrationId?: string | null;
  isNew: false;
}

export const $updateIncomingWebhookBody = joi.object<UpdateIngestorIncomingWebhookBody>({
  name: joi.string(),
  authenticationConfig: $authenticationConfigUnlessIntegration('integrationId'),
  integrationId: $integrationId,
});

export interface IncomingWebhookMappedEventConfig {
  mapTo: {
    source: EventSource;
    name: EventName;
  };
  jsonata: string;
  tags?: string[];
  counters?: string[];
  integrationId?: string;
  interactionId?: string;
  interactionJsonata?: string;
}

export type IncomingWebhookConfig = IncomingWebhookMappedEventConfig;

export type IngestedMappedEventResult = MappedEventResult[] | IngestedMappedEvent;
type MappedEventResult =
  | { status: 'success'; event: IngestedMappedEvent }
  | { status: 'error'; reason: string };

interface IngestedMappedEvent {
  id: string;
  payload: MappedEarnEvent;
  taskId: number;
  createdAt: Date;
  updatedAt: Date;
}
